import { interviewSectionTypeDisplayNames } from "@repo/app-types/entities/interviewSection/definition";
import { InterviewSectionType } from "@repo/app-types/entities/interviewSection/types";
import {
  IconHelpCircle,
  IconMessages,
  IconSchool,
  IconUsers,
} from "@tabler/icons-react";
import type { ComponentType, ReactNode } from "react";

interface InterviewSectionTypeDisplayValues {
  Icon: IconComponent;
  iconColor: string;
  title: string;
  description: ReactNode;
  skills: ReactNode[];
}

type IconComponent = ComponentType<{ className?: string }>;

const defaultInterviewSectionTypeDisplayValues: InterviewSectionTypeDisplayValues =
  {
    Icon: IconSchool,
    iconColor: "bg-gray-500",
    title: "Unknown",
    description: "",
    skills: [],
  };

const interviewSectionTypeDisplayValues: Record<
  InterviewSectionType,
  InterviewSectionTypeDisplayValues
> = {
  [InterviewSectionType.Questions]: {
    Icon: IconHelpCircle,
    iconColor: "bg-green-500",
    title: interviewSectionTypeDisplayNames[InterviewSectionType.Questions],
    description:
      "Ask candidates about their qualifications and previous work experience.",
    skills: ["Communication", "Experience", "Culture fit", "CEFR English"],
  },
  [InterviewSectionType.RolePlay]: {
    Icon: IconUsers,
    iconColor: "bg-orange-500",
    title: interviewSectionTypeDisplayNames[InterviewSectionType.RolePlay],
    description:
      "Simulate a conversation that participants will need to do on the job.",
    skills: ["Communication", "Attention to detail", "Adaptability"],
  },
  [InterviewSectionType.KnowledgeTest]: {
    Icon: IconSchool,
    iconColor: "bg-blue-500",
    title: interviewSectionTypeDisplayNames[InterviewSectionType.KnowledgeTest],
    description:
      "Test the participant's knowledge of product offerings, key processes, best practices, and more in an AI voice conversation.",
    skills: ["Experience", "Attention to detail", "Communication", "Learning"],
  },
  [InterviewSectionType.ChatQuiz]: {
    Icon: IconMessages,
    iconColor: "bg-purple-500",
    title: interviewSectionTypeDisplayNames[InterviewSectionType.ChatQuiz],
    description:
      "Conduct a short knowledge test via AI chat to help participants learn and memorize key concepts.",
    skills: ["Quick thinking", "Knowledge recall"],
  },
};

export function getInterviewSectionTypeDisplayValues(
  type: InterviewSectionType | undefined,
): InterviewSectionTypeDisplayValues {
  const values = type ? interviewSectionTypeDisplayValues[type] : undefined;
  return values ?? defaultInterviewSectionTypeDisplayValues;
}
